import { useRef, useState } from 'react';
import { api, errorMessage } from '../../lib/api';
import type { AnswerValue, FileRef, Question } from '../../lib/types';
import { formatBytes, fileUrl } from '../../lib/utils';

export interface QuestionInputProps {
  question: Question;
  value: AnswerValue | undefined;
  onChange: (value: AnswerValue) => void;
  error?: string;
  /** Link slug; required for uploads and to display the client's own attachments. */
  slug?: string;
  disabled?: boolean;
}

export function QuestionInput({
  question: q,
  value,
  onChange,
  error,
  slug,
  disabled,
}: QuestionInputProps) {
  const inputId = `q-${q.id}`;
  const describedBy = error ? `${inputId}-error` : undefined;
  const common = {
    id: inputId,
    disabled,
    'aria-invalid': error ? true : undefined,
    'aria-describedby': describedBy,
    placeholder: q.placeholder,
  };
  const str = typeof value === 'string' || typeof value === 'number' ? String(value) : '';

  switch (q.type) {
    case 'long_text':
      return (
        <textarea
          {...common}
          className="textarea"
          rows={5}
          value={str}
          maxLength={20000}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case 'short_text':
    case 'email':
    case 'phone':
    case 'number':
    case 'date': {
      const type = {
        short_text: 'text',
        email: 'email',
        phone: 'tel',
        number: 'number',
        date: 'date',
      }[q.type];
      return (
        <input
          {...common}
          className="input"
          type={type}
          inputMode={q.type === 'number' ? 'decimal' : undefined}
          autoComplete={q.type === 'email' ? 'email' : q.type === 'phone' ? 'tel' : undefined}
          value={str}
          maxLength={q.type === 'short_text' ? 1000 : undefined}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    }
    case 'dropdown':
      return (
        <select
          {...common}
          className="select"
          value={str}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">Select…</option>
          {q.options?.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      );
    case 'single_choice':
      return (
        <div className="choices" role="radiogroup" aria-describedby={describedBy}>
          {q.options?.map((o) => (
            <label key={o} className="choice">
              <input
                type="radio"
                name={inputId}
                disabled={disabled}
                checked={str === o}
                onChange={() => onChange(o)}
              />
              <span>{o}</span>
            </label>
          ))}
        </div>
      );
    case 'multiple_choice': {
      const selected = Array.isArray(value) ? (value as string[]) : [];
      return (
        <div className="choices" aria-describedby={describedBy}>
          {q.options?.map((o) => (
            <label key={o} className="choice">
              <input
                type="checkbox"
                disabled={disabled}
                checked={selected.includes(o)}
                onChange={(e) =>
                  onChange(e.target.checked ? [...selected, o] : selected.filter((s) => s !== o))
                }
              />
              <span>{o}</span>
            </label>
          ))}
        </div>
      );
    }
    case 'file_upload':
      return (
        <FileUploadInput
          files={Array.isArray(value) ? (value as FileRef[]) : []}
          onChange={onChange}
          slug={slug}
          disabled={disabled}
        />
      );
  }
}

function FileUploadInput({
  files,
  onChange,
  slug,
  disabled,
}: {
  files: FileRef[];
  onChange: (files: FileRef[]) => void;
  slug?: string;
  disabled?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(0);
  const [error, setError] = useState<string>();

  async function upload(list: FileList | null) {
    if (!list?.length || !slug) return;
    setError(undefined);
    const uploaded: FileRef[] = [];
    setUploading(list.length);
    for (const file of Array.from(list)) {
      try {
        const res = await api.upload<{ file: FileRef }>(`/public/forms/${slug}/uploads`, file);
        uploaded.push(res.file);
      } catch (err) {
        setError(`${file.name}: ${errorMessage(err)}`);
      }
      setUploading((n) => n - 1);
    }
    if (uploaded.length) onChange([...files, ...uploaded]);
    if (inputRef.current) inputRef.current.value = '';
  }

  return (
    <div className="file-upload">
      {files.length > 0 && (
        <ul className="file-list">
          {files.map((f) => (
            <li key={f.id}>
              {f.contentType.startsWith('image/') ? (
                <img src={fileUrl(f.id, slug)} alt="" className="file-thumb" />
              ) : (
                <span className="file-icon">PDF</span>
              )}
              <span className="file-name">
                {f.name} <span className="muted">({formatBytes(f.size)})</span>
              </span>
              {!disabled && (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => onChange(files.filter((x) => x.id !== f.id))}
                >
                  Remove
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {!disabled && (
        <>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={!slug || uploading > 0}
            onClick={() => inputRef.current?.click()}
          >
            {uploading > 0
              ? `Uploading ${uploading}…`
              : files.length
                ? 'Add more files'
                : 'Upload files'}
          </button>
          {!slug && <p className="hint">Uploads are disabled in preview.</p>}
          <p className="hint">Images (JPG, PNG, WEBP, GIF) or PDF.</p>
          <input
            ref={inputRef}
            type="file"
            multiple
            hidden
            accept="image/png,image/jpeg,image/webp,image/gif,application/pdf"
            onChange={(e) => void upload(e.target.files)}
          />
        </>
      )}
      {error && <p className="error-text">{error}</p>}
    </div>
  );
}
